import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { CentrexExtensionModel } from '../models/centrex-extension.model';
import { ApiService } from './api.service';
import { ServiceBase } from './service-base';

@Injectable({
  providedIn: 'root',
})
export class CentrexExtensionsService extends ServiceBase {
  private extensionsCache = new Map<
    string,
    Observable<CentrexExtensionModel[]>
  >();

  private groupsCache = new Map<string, Observable<CentrexExtensionModel[]>>();

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) {
    super();
  }

  private createHeaders(additionalHeaders?: {
    [key: string]: string;
  }): HttpHeaders {
    const token = this.apiService.getAccessToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach((key) => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }

    return headers;
  }

  private handleError(error: any): Observable<never> {
    console.error('Erreur CentrexExtensionsService', error);
    return throwError(() => error);
  }

  getExtensions(
    organizationId: string,
    platform: string,
  ): Observable<CentrexExtensionModel[]> {
    const cacheKey = `${organizationId}_${platform}`;

    // retourne le cache si présent
    if (this.extensionsCache.has(cacheKey)) {
      return this.extensionsCache.get(cacheKey)!;
    }

    const url = `${this.apiUrl}/api/centrex/extensions`;

    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    const request$ = this.http
      .get<CentrexExtensionModel[]>(url, {
        headers,
      })
      .pipe(
        shareReplay(1),

        catchError((error) => {
          this.extensionsCache.delete(cacheKey);
          return this.handleError(error);
        }),
      );

    this.extensionsCache.set(cacheKey, request$);

    return request$;
  }

  getExtensionById(
    organizationId: string,
    platform: string,
    extensionId: string,
  ): Observable<CentrexExtensionModel> {
    const url = `${this.apiUrl}/api/centrex/extensions/${extensionId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http
      .get<CentrexExtensionModel>(url, { headers })
      .pipe(catchError(this.handleError));
  }

  getExtensionsByIds(
    organizationId: string,
    platform: string,
    extensionIds: string[],
  ): Observable<CentrexExtensionModel[]> {
    return forkJoin(
      extensionIds.map((id) =>
        this.getExtensionById(organizationId, platform, id),
      ),
    );
  }

  getExtensionGroups(
    organizationId: string,
    platform: string,
  ): Observable<CentrexExtensionModel[]> {
    const cacheKey = `${organizationId}_${platform}`;

    // retourne le cache si présent
    if (this.groupsCache.has(cacheKey)) {
      return this.groupsCache.get(cacheKey)!;
    }

    const url = `${this.apiUrl}/api/centrex/extensiongroups`;

    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    const request$ = this.http
      .get<CentrexExtensionModel[]>(url, {
        headers,
      })
      .pipe(
        shareReplay(1),

        catchError((error) => {
          this.groupsCache.delete(cacheKey);
          return this.handleError(error);
        }),
      );

    this.groupsCache.set(cacheKey, request$);

    return request$;
  }

  createExtension(
    organizationId: string,
    platform: string,
    extension: CentrexExtensionModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.post<boolean>(url, extension, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.extensionsCache.delete(cacheKey);
        this.groupsCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  updateExtension(
    organizationId: string,
    platform: string,
    extensionId: string,
    extension: CentrexExtensionModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions/${extensionId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.patch<boolean>(url, extension, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.extensionsCache.delete(cacheKey);
        this.groupsCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  deleteExtension(
    organizationId: string,
    platform: string,
    extensionId: string,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/extensions/${extensionId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.delete<boolean>(url, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.extensionsCache.delete(cacheKey);
        this.groupsCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  clearCache(): void {
    this.extensionsCache.clear();
    this.groupsCache.clear();
  }
}
